/**
 * Tour API service
 */

import { apiClient } from "@/lib/api-client"

export interface TourData {
  id: string
  tourType: string
  status: "queued" | "started" | "in_progress" | "completed" | "skipped"
  currentScreen: number
  triggerCondition: string | null
  metadata: Record<string, unknown> | null
}

export interface TourStatus {
  active: TourData | null
  queued: TourData | null
  exists: boolean
}

export interface AllTourStatus {
  home_tour: TourStatus
  ritual_tour: TourStatus
}

// --- Mutations ---

export async function startTour(
  tourType: string,
  metadata?: Record<string, unknown>,
): Promise<TourData> {
  const res = await apiClient.post<{ success: boolean; data: TourData }>("/api/user/tours", {
    tourType,
    ...(metadata ? { metadata } : {}),
  })
  return res.data.data
}

export async function queueTour(
  tourType: string,
  triggerCondition: string,
  metadata?: Record<string, unknown>,
): Promise<TourData> {
  const res = await apiClient.post<{ success: boolean; data: TourData }>("/api/user/tours/queue", {
    tourType,
    triggerCondition,
    ...(metadata ? { metadata } : {}),
  })
  return res.data.data
}

// --- Unified status ---

/**
 * Get tour status. When tourType is provided, returns status for that type.
 * When omitted, returns status for both home_tour and ritual_tour in one call.
 */
export async function getTourStatus(tourType?: string): Promise<TourStatus> {
  const params = tourType ? `?type=${tourType}` : ""
  const res = await apiClient.get<{ success: boolean; data: TourStatus }>(
    `/api/user/tours${params}`,
  )
  return res.data.data
}

/**
 * Get status for all tour types in one API call.
 * Returns { home_tour, ritual_tour } with active/queued/exists for each.
 */
export async function getAllTourStatus(): Promise<AllTourStatus> {
  const res = await apiClient.get<{ success: boolean; data: AllTourStatus }>("/api/user/tours")
  return res.data.data
}

// --- Unified update ---

export async function updateTour(
  tourId: string,
  action: { action: "progress" | "complete" | "skip"; currentScreen?: number },
): Promise<TourData> {
  const res = await apiClient.put<{ success: boolean; data: TourData }>(
    `/api/user/tours/${tourId}`,
    action,
  )
  return res.data.data
}

// --- Demo ritual ---

export async function getDemoRitualId(): Promise<string | null> {
  try {
    const res = await apiClient.get<{ success: boolean; data: { id: string } | null }>(
      "/api/rituals/tour-demo",
    )
    return res.data.data?.id ?? null
  } catch {
    return null
  }
}
